import type { TFunction } from "react-i18next";
import { isBlank } from "utils/string";
import { isEmptyArray } from "utils/isEmptyArray";
import { FormState } from "../types";

export const NAME_MAX_LENGTH = 100;
export const DESCRIPTION_MAX_LENGTH = 500;

export type MappingErrors = {
  key?: string;
  value?: string;
};

export type FormErrors = {
  name?: string;
  description?: string;
  storageAccount?: string;
  metadataKey?: string;
  mappings?: MappingErrors[];
};

export type MappingTouched = {
  key?: boolean;
  value?: boolean;
};

export type FormTouched = {
  name?: boolean;
  description?: boolean;
  storageAccount?: boolean;
  metadataKey?: boolean;
  mappings?: MappingTouched[];
};

type ValidateOptions = {
  skipMappings?: boolean;
};

function isMappingValueBlank(value: string | string[]): boolean {
  if (Array.isArray(value)) return isEmptyArray(value);
  return isBlank(value);
}

export function validate(
  values: FormState,
  t: TFunction,
  options: ValidateOptions = {}
): FormErrors {
  const errors: FormErrors = {};

  if (isBlank(values.name)) {
    errors.name = t("form.name.required");
  } else if (values.name.length > NAME_MAX_LENGTH) {
    errors.name = t("form.name.maxLength", { max: NAME_MAX_LENGTH });
  }

  if (
    !isBlank(values.description) &&
    values.description.length > DESCRIPTION_MAX_LENGTH
  ) {
    errors.description = t("form.description.maxLength", {
      max: DESCRIPTION_MAX_LENGTH,
    });
  }

  if (isBlank(values.storageAccount)) {
    errors.storageAccount = t("form.storageAccount.required");
  }

  if (isBlank(values.metadataKey)) {
    errors.metadataKey = t("form.metadataKey.required");
  }

  if (!options.skipMappings) {
    const mappingErrors = values.mappings.map(m => {
      const e: MappingErrors = {};

      if (isBlank(m.key)) {
        e.key = t("form.mappings.key.required");
      } else if (isMappingValueBlank(m.value)) {
        e.value = t("form.mappings.value.required");
      }

      return e;
    });

    if (mappingErrors.some(e => Object.keys(e).length > 0)) {
      errors.mappings = mappingErrors;
    }
  }

  return errors;
}

export function hasErrors(errors: FormErrors): boolean {
  return Object.keys(errors).length > 0;
}
